function createHsbdFinishingRecovery(request, makeToken, panelVariantId = 45579274813593) {
  const attemptProperty = '_HSBD finishing attempt';
  const linkProperty = '_Linked shirt line';

  function begin(input) {
    if (!Number.isSafeInteger(input.id) || input.id <= 0 ||
        !Number.isInteger(input.quantity) || input.quantity < 1 ||
        input.properties?.['Add panty panel'] !== 'YES') throw new Error('Invalid finishing selection');
    const token = makeToken();
    if (typeof token !== 'string' || !token) throw new Error('Missing finishing attempt');
    return {
      token, seenShirt: false, complete: false,
      item: { ...input, properties: { ...input.properties, [attemptProperty]: token } }
    };
  }

  function findAttempt(cart, attempt) {
    const shirts = cart.items.filter(item =>
      item.variant_id === attempt.item.id && item.properties?.[attemptProperty] === attempt.token
    );
    if (shirts.length > 1) throw new Error('Ambiguous finishing attempt');
    return shirts[0];
  }

  function panelsFor(cart, shirt) {
    const token = shirt.properties?.[attemptProperty];
    return cart.items.filter(item => item.variant_id === panelVariantId && (
      item.properties?.[linkProperty] === shirt.key ||
      (token && item.properties?.[attemptProperty] === token)
    ));
  }

  function inspect(cart, knownShirtKeys = []) {
    const issues = [];
    const shirts = cart.items.filter(item => item.variant_id !== panelVariantId && (
      item.properties?.[attemptProperty] || knownShirtKeys.includes(item.key) || cart.items.some(panel =>
        panel.variant_id === panelVariantId && panel.properties?.[linkProperty] === item.key
      )
    ));
    for (const shirt of shirts) {
      const panels = panelsFor(cart, shirt);
      const expected = shirt.properties?.['Add panty panel'] === 'YES' ? shirt.quantity : 0;
      const actual = panels.reduce((total, item) => total + item.quantity, 0);
      if (actual !== expected || panels.length > 1 || panels.some(p => p.properties?.[linkProperty] !== shirt.key)) {
        issues.push({ key: shirt.key, expected, actual, reason: 'fee_mismatch' });
      }
    }
    for (const panel of cart.items.filter(item => item.variant_id === panelVariantId && item.properties?.[linkProperty])) {
      if (!shirts.some(shirt => panelsFor(cart, shirt).some(item => item.key === panel.key))) {
        issues.push({ key: panel.key, reason: 'orphan_fee' });
      }
    }
    return issues;
  }

  async function writeAndVerify(path, body, accepted) {
    let error = null;
    try { await request(path, body); } catch (e) { error = e; }
    const cart = await request('cart.js');
    if (!accepted(cart)) throw error || new Error('Finishing update could not be verified');
    return cart;
  }

  async function reconcile(cart, shirt) {
    const quantity = shirt.properties?.['Add panty panel'] === 'YES' ? shirt.quantity : 0;
    let panels = panelsFor(cart, shirt);
    if (!panels.length && quantity > 0) {
      const properties = { For: shirt.product_title, [linkProperty]: shirt.key };
      if (shirt.properties?.[attemptProperty]) properties[attemptProperty] = shirt.properties[attemptProperty];
      cart = await writeAndVerify('cart/add.js', { id: panelVariantId, quantity, properties }, current =>
        panelsFor(current, shirt).reduce((total, item) => total + item.quantity, 0) === quantity
      );
      panels = panelsFor(cart, shirt);
    }
    for (let index = 0; index < panels.length; index++) {
      const panel = cart.items.find(item => item.key === panels[index].key);
      if (!panel) continue;
      const desiredQuantity = index === 0 ? quantity : 0;
      const properties = { ...panel.properties, [linkProperty]: shirt.key };
      if (shirt.properties?.[attemptProperty]) properties[attemptProperty] = shirt.properties[attemptProperty];
      if (panel.quantity === desiredQuantity &&
          JSON.stringify(panel.properties) === JSON.stringify(properties)) continue;
      cart = await writeAndVerify('cart/change.js', { id: panel.key, quantity: desiredQuantity, properties }, current => {
        if (desiredQuantity === 0) return !current.items.some(item => item.key === panel.key);
        return panelsFor(current, shirt).some(item => item.quantity === desiredQuantity &&
          item.properties?.[linkProperty] === shirt.key &&
          Object.keys(properties).every(key => item.properties?.[key] === properties[key]));
      });
    }
    if (inspect(cart).some(issue => issue.key === shirt.key)) throw new Error('Finishing fee remains inconsistent');
    return cart;
  }

  async function submitOnce(attempt) {
    let cart = await request('cart.js');
    let shirt = findAttempt(cart, attempt);
    if (!shirt) {
      if (attempt.seenShirt || attempt.complete) throw new Error('Finishing shirt was removed; review your cart');
      cart = await writeAndVerify('cart/add.js', attempt.item, current => Boolean(findAttempt(current, attempt)));
      shirt = findAttempt(cart, attempt);
    }
    attempt.seenShirt = true;
    if (shirt.quantity !== attempt.item.quantity) throw new Error('Shirt quantity changed; review your cart');
    cart = await reconcile(cart, shirt);
    attempt.complete = true;
    return { item: findAttempt(cart, attempt), cart };
  }

  function submit(attempt) {
    if (!attempt.inFlight) {
      attempt.inFlight = submitOnce(attempt).finally(() => { attempt.inFlight = null; });
    }
    return attempt.inFlight;
  }

  async function changeQuantity(key, quantity) {
    if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Invalid shirt quantity');
    const before = await request('cart.js');
    const original = before.items.find(item => item.key === key);
    if (!original) return before;
    const panels = panelsFor(before, original);
    let changeError = null;
    try { await request('cart/change.js', { id: key, quantity }); } catch (e) { changeError = e; }
    let cart = await request('cart.js');
    const updated = cart.items.find(item => item.key === original.key) || cart.items.find(item =>
      item.variant_id === original.variant_id &&
      JSON.stringify(item.properties || {}) === JSON.stringify(original.properties || {})
    );
    if (quantity === 0 && !updated) {
      for (const panel of panels) {
        if (cart.items.some(item => item.key === panel.key)) {
          cart = await writeAndVerify('cart/change.js', { id: panel.key, quantity: 0 }, current =>
            !current.items.some(item => item.key === panel.key));
        }
      }
    } else if (updated) {
      const oldPanels = panels.filter(panel => cart.items.some(item => item.key === panel.key));
      for (const panel of oldPanels) {
        if (panel.properties?.[linkProperty] !== updated.key) {
          cart = await writeAndVerify('cart/change.js', {
            id: panel.key, quantity: panel.quantity,
            properties: { ...panel.properties, [linkProperty]: updated.key }
          }, current => panelsFor(current, updated).some(item =>
            item.properties?.[linkProperty] === updated.key));
        }
      }
      cart = await reconcile(cart, updated);
    } else throw changeError || new Error('Shirt update could not be verified');
    if (changeError && (updated?.quantity || 0) !== quantity) {
      changeError.hsbdCart = cart;
      throw changeError;
    }
    return cart;
  }

  return { begin, submit, inspect, reconcile, changeQuantity };
}
